import { Trophy, TrendingDown, CheckCircle2, AlertTriangle, RotateCcw, Lightbulb, X } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { fmt } from "@/poker/cards";
import { ordinal } from "@/poker/engine";

const Metric = ({ label, value, sub, testId }) => (
  <div className="p-4 rounded-xl bg-white/[0.03] border border-white/10">
    <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">{label}</div>
    <div data-testid={testId} className="font-mono text-2xl font-bold text-amber-400 mt-1">{value}</div>
    {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
  </div>
);

const Notes = ({ title, items, icon: Icon, color, testId, empty }) => (
  <div data-testid={testId} className="p-5 rounded-2xl bg-[#12141C] border border-white/10 space-y-3">
    <h3 className={`font-heading text-xl uppercase tracking-wide flex items-center gap-2 ${color}`}><Icon size={18} />{title}</h3>
    {items.length === 0 && <p className="text-sm text-slate-500">{empty}</p>}
    {items.map((x, i) => <p key={i} className="text-sm text-slate-300 leading-relaxed border-l-2 border-white/10 pl-3">{x.text || x}</p>)}
  </div>
);

export const SummaryScreen = ({ s, onAgain, onClose }) => {
  const champ = s.place === 1;
  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-black/75 backdrop-blur-md px-3 sm:px-8 py-6 sm:py-10">
      <div data-testid="end-tournament-summary" role="dialog" aria-modal="true" className="relative max-w-6xl mx-auto space-y-8 setup-bg rounded-2xl border border-white/10 shadow-2xl p-5 sm:p-10 fade-up">
        <button data-testid="close-summary-x" onClick={onClose} aria-label="Close summary" className="absolute top-3 right-3 p-2 rounded-full text-slate-400 hover:text-white hover:bg-white/10 transition-colors"><X size={20} /></button>
        <div className="flex flex-col sm:flex-row sm:items-end gap-6 fade-up">
          <div className={`w-20 h-20 rounded-2xl flex items-center justify-center ${champ ? "bg-amber-500 text-black" : "bg-white/5 text-slate-300 border border-white/10"}`}>
            {champ ? <Trophy size={40} /> : <TrendingDown size={36} />}
          </div>
          <div className="flex-1">
            <div className="text-xs uppercase tracking-[0.3em] text-slate-500">Tournament complete</div>
            <h1 data-testid="finish-position" className="font-heading text-5xl sm:text-6xl font-extrabold uppercase tracking-tight text-white">
              {champ ? "Champion" : `${ordinal(s.place)} of ${s.total}`}
            </h1>
            <p data-testid="bust-info" className="text-slate-400 text-sm mt-1">
              {champ ? `You won after ${s.hands} hands, reaching level ${s.finalLevel} (${s.finalBlinds}).` :
                s.bustInfo ? `Eliminated on hand #${s.bustInfo.hand} by ${s.bustInfo.by}${s.bustInfo.handName ? ` (${s.bustInfo.handName})` : ""} holding ${s.bustInfo.cards}${s.bustInfo.board ? ` on ${s.bustInfo.board}` : ""}.` : ""}
            </p>
          </div>
          <div className="flex flex-wrap gap-2 sm:mr-10">
            <button data-testid="play-again-button" onClick={onAgain} className="flex items-center gap-2 px-5 h-12 rounded-xl bg-amber-500 hover:bg-amber-400 text-black font-heading text-lg uppercase font-bold transition-colors"><RotateCcw size={18} />Play again</button>
            <button data-testid="close-summary-button" onClick={onClose} className="flex items-center gap-2 px-5 h-12 rounded-xl border border-white/15 text-slate-200 hover:bg-white/5 font-heading text-lg uppercase font-bold transition-colors"><X size={18} />Close &amp; New Game</button>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Metric label="Hands dealt" value={s.dealt} testId="metric-hands" />
          <Metric label="Hands won" value={s.handsWon} sub={`${s.winRate}% of hands`} testId="metric-won" />
          <Metric label="VPIP / PFR" value={`${s.vpip}/${s.pfr}`} sub="% voluntarily played / raised" testId="metric-vpip-pfr" />
          <Metric label="Aggression" value={s.af.toFixed(1)} sub="post-flop bets+raises per call" testId="metric-af" />
          <Metric label="Showdowns" value={`${s.sdWon}/${s.showdowns}`} sub="won / reached" testId="metric-showdowns" />
          <Metric label="Biggest pot" value={fmt(s.biggestPot)} testId="metric-biggest-pot" />
          <Metric label="Decisions" value={s.decisionsCount} testId="metric-decisions" />
          <Metric label="Post-flop accuracy" value={s.postflopAccuracy === null ? "—" : `${s.postflopAccuracy}%`} sub="vs. pot odds & equity" testId="metric-accuracy" />
        </div>

        <div className="grid lg:grid-cols-3 gap-4">
          <Notes testId="feedback-leaks" title="Leaks to fix" items={s.leaks} icon={AlertTriangle} color="text-red-400" empty="No clear mistakes detected. Nice discipline." />
          <Notes testId="feedback-good" title="Good decisions" items={s.goods} icon={CheckCircle2} color="text-emerald-400" empty="Play a few more hands to collect highlights." />
          <Notes testId="feedback-tips" title="Style coaching" items={s.tips} icon={Lightbulb} color="text-cyan-300" empty="" />
        </div>

        <div className="grid lg:grid-cols-[1.4fr_1fr] gap-4">
          <div className="p-5 rounded-2xl bg-[#12141C] border border-white/10">
            <h3 className="font-heading text-xl uppercase tracking-wide text-slate-100 mb-3">Your stack</h3>
            <div data-testid="chip-history-chart" className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={s.chipHistory}>
                  <XAxis dataKey="hand" stroke="#475569" fontSize={11} />
                  <YAxis stroke="#475569" fontSize={11} width={50} />
                  <Tooltip contentStyle={{ background: "#0d0f15", border: "1px solid #334155" }} />
                  <Line type="monotone" dataKey="stack" stroke="#f59e0b" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
          <div className="p-5 rounded-2xl bg-[#12141C] border border-white/10 overflow-x-auto">
            <h3 className="font-heading text-xl uppercase tracking-wide text-slate-100 mb-3">The field</h3>
            <table data-testid="ai-breakdown-table" className="w-full text-sm">
              <thead><tr className="text-[10px] uppercase tracking-wider text-slate-500 text-left"><th className="py-1">Player</th><th>Level</th><th>VPIP</th><th>PFR</th><th>Finish</th></tr></thead>
              <tbody>
                {s.field.map((f) => (
                  <tr key={f.name} className="border-t border-white/5 text-slate-300">
                    <td className="py-2 font-semibold">{f.name}</td><td>{f.skill}</td><td className="font-mono">{f.vpip}%</td><td className="font-mono">{f.pfr}%</td>
                    <td className="font-mono">{f.place ? ordinal(f.place) : `In · ${fmt(f.stack)}`}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};
